import { Component, ViewChild } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TableSummaryComponent } from '../table-summary/table-summary.component';
import { AuthService } from 'src/app/shared/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { SummaryService } from './summary.service';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent {
  startRaw = '';
  endRaw = '';

  resumo = new FormGroup({
    initialDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    endDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
  });

  public isSubmitted = false;
  public isLoaded = false;

  private accountTimezoneOffsetMinutes: number | null = null;
  private originCurrency: string = 'BRL';

  @ViewChild(TableSummaryComponent, { static: true }) table!: TableSummaryComponent;

  constructor(
    private toastr: ToastrService,
    private authService: AuthService,
    private summaryService: SummaryService
  ) { }

  private parseTimezoneOffset(tz: string | null | undefined): number | null {
    if (!tz) return null;

    const m = /^([+-]?)(\d{1,2}):(\d{2})$/.exec(tz.trim());
    if (!m) return null;

    const sign = m[1] === '-' ? 1 : -1;
    const hours = Number(m[2]);
    const minutes = Number(m[3]);

    return sign * (hours * 60 + minutes);
  }

  private nowInAccountTz(): Date {
    const now = new Date();

    if (this.accountTimezoneOffsetMinutes == null) {
      return now;
    }

    const browserOffset = now.getTimezoneOffset();
    const diffMinutes = this.accountTimezoneOffsetMinutes - browserOffset;

    return new Date(now.getTime() - diffMinutes * 60000);
  }

  private pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  private formatBr(d: Date): string {
    return `${this.pad(d.getDate())}/${this.pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  private startOfDay(v: any): Date {
    const d = this.asDate(v);
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  }

  private endOfDay(v: any): Date {
    const d = this.asDate(v);
    const copy = new Date(d);
    copy.setHours(23, 59, 59, 999);
    return copy;
  }

  private ymd(d: Date): string {
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
  }

  private toIsoStart(d: Date): string {
    return `${this.ymd(d)}T00:00:00`;
  }

  private toIsoEnd(d: Date): string {
    return `${this.ymd(d)}T23:59:59`;
  }

  private toNgb(v: any): NgbDateStruct {
    const d = this.asDate(v);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  private parseDate(s: string): Date | null {
    if (!s) return null;

    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;

    const d = new Date(+m[3], +m[2] - 1, +m[1], 0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  parseDateInput(ctrlName: 'initialDate' | 'endDate', raw: string): Date | null {
    const ctrl = this.resumo.controls[ctrlName];

    const parsed = this.parseDate(raw);
    if (parsed) {
      ctrl.setValue(parsed, { emitEvent: false });
      ctrl.setErrors(null);
      return parsed;
    }

    if (raw?.trim()) {
      ctrl.setErrors({ matDatepickerParse: { text: raw } });
    } else {
      ctrl.setValue(null, { emitEvent: false });
      ctrl.setErrors({ required: true });
    }

    return null;
  }

  onDateChange(ctrlName: 'initialDate' | 'endDate', raw: string): void {
    const parsed = this.parseDateInput(ctrlName, raw);
    if (!parsed) return;

    const normalized = this.formatBr(parsed);
    if (ctrlName === 'initialDate') this.startRaw = normalized;
    else this.endRaw = normalized;
  }

  private fixDatesFromUI(startRaw?: string, endRaw?: string): void {
    if (startRaw !== undefined) this.parseDateInput('initialDate', startRaw);
    if (endRaw !== undefined) this.parseDateInput('endDate', endRaw);
  }

  private buildDefaultRange(): { start: Date; end: Date } {
    const origin = (this.originCurrency || 'BRL').toUpperCase();
    const baseDate = origin === 'BRL' ? new Date() : this.nowInAccountTz();

    const start = new Date(baseDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(baseDate);
    end.setHours(0, 0, 0, 0);

    return { start, end };
  }

  async ngOnInit(): Promise<void> {
    try {
      const account = await firstValueFrom(this.authService.getAccount());

      this.originCurrency =
        ((account as any)?.originCurrency ||
          this.authService.getOriginCurrency?.() ||
          'BRL').toUpperCase();

      this.accountTimezoneOffsetMinutes = this.parseTimezoneOffset(
        (account as any)?.timezone
      );

      const { start, end } = this.buildDefaultRange();

      this.resumo.controls.initialDate.setValue(start);
      this.resumo.controls.endDate.setValue(end);

      this.startRaw = this.formatBr(start);
      this.endRaw = this.formatBr(end);

      this.buscar(this.startRaw, this.endRaw);
    } catch {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      this.resumo.controls.initialDate.setValue(now);
      this.resumo.controls.endDate.setValue(now);

      this.startRaw = this.formatBr(now);
      this.endRaw = this.formatBr(now);

      this.buscar(this.startRaw, this.endRaw);
    }
  }

  buscar(startRaw?: string, endRaw?: string) {
    this.fixDatesFromUI(startRaw, endRaw);

    this.resumo.markAllAsTouched();
    this.resumo.updateValueAndValidity({ emitEvent: false });

    const start = this.resumo.controls.initialDate.value as Date | null;
    const end = this.resumo.controls.endDate.value as Date | null;

    if (!start || !end) return;

    if (start > end) {
      this.toastr.error(
        'Data inicial deve ser menor ou igual a data final!',
        'Erro',
        {
          timeOut: 3000,
          positionClass: 'toast-top-right',
        }
      );
      return;
    }

    if (this.resumo.invalid) return;

    this.isSubmitted = true;
    this.isLoaded = false;

    const startDate = this.startOfDay(start);
    const endDate = this.endOfDay(end);

    this.table.dataInitial = this.toNgb(startDate);
    this.table.dataFinal = this.toNgb(endDate);

    this.table.startDateIso = this.toIsoStart(startDate);
    this.table.endDateIso = this.toIsoEnd(endDate);

    this.table.getTransactions();

    this.isSubmitted = false;
  }

  downloadFile() {
    const start = this.resumo.controls.initialDate.value as Date | null;
    const end = this.resumo.controls.endDate.value as Date | null;

    if (!start || !end) {
      return;
    }

    const startDate = this.startOfDay(start);
    const endDate = this.endOfDay(end);

    const startDateIso = this.toIsoStart(startDate);
    const endDateIso = this.toIsoEnd(endDate);

    const formatDate = (date: Date | string): string => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };

    this.isLoaded = false;

    this.summaryService.exportDataPdf(startDateIso, endDateIso).subscribe((value) => {
      const byteCharacters = atob(value);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: 'application/pdf',
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      const company = this.authService.getCompany();

      const formattedStart = formatDate(startDate);
      const formattedEnd = formatDate(endDate);

      a.href = url;
      a.download = `${company}_resumo_${formattedStart}_${formattedEnd}.pdf`;
      a.click();

      window.URL.revokeObjectURL(url);
      a.remove();
      this.isLoaded = true;
    });
  }
  
}